import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Mail, Send, Eye, Search, CheckCircle, XCircle, Loader2,
  Users, UserPlus, Globe, ChevronDown, ChevronUp, RefreshCw, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  onboarding: 'Onboarding',
  promotion: 'Promotion',
  membership: 'Membership',
  loyalty: 'Loyalty',
  announcement: 'Announcement',
};

const CATEGORY_COLORS = {
  onboarding: 'text-green-400 bg-green-500/10 border-green-500/20',
  promotion: 'text-[#E8720C] bg-[#E8720C]/10 border-[#E8720C]/20',
  membership: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
  loyalty: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
  announcement: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
};

const CampaignsPanelV2 = () => {
  const [tab, setTab] = useState('create');
  const [history, setHistory] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateLang, setTemplateLang] = useState('es');

  // Campaign fields
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [recipientType, setRecipientType] = useState('all_verified');
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [countryFilter, setCountryFilter] = useState('');
  const [contactSearch, setContactSearch] = useState('');
  const [senderType, setSenderType] = useState('notification');
  const [testEmail, setTestEmail] = useState('');
  const [recipientCount, setRecipientCount] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);

  useEffect(() => {
    loadHistory();
    loadTemplates();
    loadRecipientCount();
  }, []);

  useEffect(() => { loadRecipientCount(); }, [recipientType, countryFilter, selectedEmails]);

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/campaigns`, { withCredentials: true });
      setHistory(r.data || []);
    } catch {}
  };

  const loadTemplates = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/email-templates`, { withCredentials: true });
      setTemplates(r.data || []);
    } catch {}
  };

  const loadContacts = useCallback(async () => {
    if (contacts.length > 0) return;
    setLoadingContacts(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/verified-contacts`, { withCredentials: true });
      setContacts(r.data || []);
    } catch {}
    setLoadingContacts(false);
  }, [contacts.length]);

  const loadRecipientCount = async () => {
    if (recipientType === 'selected' || selectedEmails.length > 0) {
      setRecipientCount(selectedEmails.length);
      return;
    }
    try {
      const r = await axios.get(`${API_URL}/api/admin/campaign-recipients`, {
        withCredentials: true,
        params: { recipient_type: recipientType, country: countryFilter }
      });
      setRecipientCount(r.data.count);
    } catch {}
  };

  const handleSelectTemplate = (t) => {
    setSelectedTemplate(t);
    const lang = templateLang;
    setSubject(t.subject[lang] || t.subject.es || '');
    setHtmlBody(t.body[lang] || t.body.es || '');
    setResult(null);
  };

  const handleTemplateLangChange = (lang) => {
    setTemplateLang(lang);
    if (selectedTemplate) {
      setSubject(selectedTemplate.subject[lang] || selectedTemplate.subject.es || '');
      setHtmlBody(selectedTemplate.body[lang] || selectedTemplate.body.es || '');
    }
  };

  const toggleContact = (email) => {
    setSelectedEmails(prev =>
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
    if (recipientType !== 'selected') setRecipientType('selected');
  };

  const toggleAllContacts = () => {
    const filtered = getFilteredContacts();
    const allSelected = filtered.every(c => selectedEmails.includes(c.email));
    if (allSelected) {
      const filteredEmails = new Set(filtered.map(c => c.email));
      setSelectedEmails(prev => prev.filter(e => !filteredEmails.has(e)));
    } else {
      const newEmails = new Set([...selectedEmails, ...filtered.map(c => c.email)]);
      setSelectedEmails([...newEmails]);
    }
    if (recipientType !== 'selected') setRecipientType('selected');
  };

  const getFilteredContacts = () => {
    return contacts.filter(c => {
      const q = contactSearch.toLowerCase();
      if (q && !c.name.toLowerCase().includes(q) && !c.email.toLowerCase().includes(q) && !c.country.toLowerCase().includes(q)) return false;
      return true;
    });
  };

  const handleSend = async (action) => {
    if (!subject.trim() || !htmlBody.trim()) {
      setResult({ error: 'Subject and body are required.' });
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const payload = {
        subject, html_body: htmlBody, recipient_type: recipientType,
        country_filter: countryFilter, sender_type: senderType,
        action, test_email: testEmail,
        selected_emails: recipientType === 'selected' ? selectedEmails : [],
        language: templateLang,
      };
      const r = await axios.post(`${API_URL}/api/admin/campaigns`, payload, { withCredentials: true });
      setResult(r.data);
      if (action !== 'save') loadHistory();
    } catch (e) {
      setResult({ error: e.response?.data?.detail || 'Send failed.' });
    }
    setSending(false);
  };

  const previewHTML = htmlBody
    .replace(/\{\{name\}\}/g, 'Joel')
    .replace(/\{\{email\}\}/g, 'joel@example.com')
    .replace(/\{\{site_url\}\}/g, API_URL || '#');

  const filteredContacts = getFilteredContacts();

  return (
    <div data-testid="admin-campaigns-section">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">Email Campaigns</h2>
          <p className="text-sm text-white/40 mt-1">Create, preview, and send targeted email campaigns</p>
        </div>
        <button onClick={() => { loadHistory(); loadTemplates(); }} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
          <RefreshCw className="w-4 h-4 text-white/50" />
        </button>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { id: 'create', label: 'Create Campaign' },
          { id: 'templates', label: `Templates (${templates.length})` },
          { id: 'history', label: `History (${history.length})` },
        ].map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); if (t.id === 'history') loadHistory(); }}
            className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${tab === t.id ? 'bg-[#E8720C] text-white' : 'bg-white/5 text-white/50 hover:text-white/70'}`}
            data-testid={`campaigns-tab-${t.id}`}
          >{t.label}</button>
        ))}
      </div>

      {/* ─── TEMPLATES TAB ─── */}
      {tab === 'templates' && (
        <div>
          <div className="flex gap-2 mb-4">
            {['es', 'en', 'pt'].map(lang => (
              <button key={lang} onClick={() => setTemplateLang(lang)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${templateLang === lang ? 'bg-[#E8720C]/15 border border-[#E8720C]/30 text-[#E8720C]' : 'bg-white/5 border border-white/10 text-white/40'}`}
                data-testid={`template-lang-${lang}`}
              >{lang.toUpperCase()}</button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {templates.map(t => {
              const catClass = CATEGORY_COLORS[t.category] || 'text-white/50 bg-white/5 border-white/10';
              return (
                <div key={t.id} className={`glass-card p-4 cursor-pointer transition-all hover:border-[#E8720C]/30 ${selectedTemplate?.id === t.id ? 'border-[#E8720C]/40 ring-1 ring-[#E8720C]/20' : ''}`}
                  onClick={() => handleSelectTemplate(t)}
                  data-testid={`template-card-${t.id}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-[9px] px-2 py-0.5 rounded-full border font-medium ${catClass}`}>
                      {CATEGORY_LABELS[t.category] || t.category}
                    </span>
                    {selectedTemplate?.id === t.id && <CheckCircle className="w-3.5 h-3.5 text-[#E8720C] ml-auto" />}
                  </div>
                  <p className="text-sm font-medium mb-1">{t.name[templateLang] || t.name.es}</p>
                  <p className="text-[11px] text-white/40 truncate">{t.subject[templateLang] || t.subject.es}</p>
                  {/* Mini preview */}
                  <div className="mt-3 p-3 rounded-lg bg-[#0a0a0c] border border-white/5 max-h-[80px] overflow-hidden"
                    style={{ fontSize: '8px', color: '#999' }}
                    dangerouslySetInnerHTML={{ __html: (t.body[templateLang] || t.body.es || '').replace(/\{\{name\}\}/g, 'Joel').replace(/\{\{site_url\}\}/g, '#') }}
                  />
                </div>
              );
            })}
          </div>
          {selectedTemplate && (
            <div className="mt-4 flex gap-2">
              <button onClick={() => { setTab('create'); }}
                className="px-5 py-2.5 btn-primary text-xs font-semibold" data-testid="template-use-btn">
                Use Selected Template
              </button>
              <button onClick={() => setSelectedTemplate(null)}
                className="px-4 py-2.5 btn-secondary text-xs" data-testid="template-clear-btn">
                Clear
              </button>
            </div>
          )}
        </div>
      )}

      {/* ─── CREATE TAB ─── */}
      {tab === 'create' && (
        <div className="space-y-4">
          {/* Template indicator */}
          {selectedTemplate && (
            <div className="flex items-center gap-3 p-3 rounded-xl bg-[#E8720C]/5 border border-[#E8720C]/15">
              <Mail className="w-4 h-4 text-[#E8720C] shrink-0" />
              <span className="text-xs text-[#E8720C]">Using template: <strong>{selectedTemplate.name[templateLang] || selectedTemplate.name.es}</strong></span>
              <div className="ml-auto flex gap-1.5">
                {['es', 'en', 'pt'].map(lang => (
                  <button key={lang} onClick={() => handleTemplateLangChange(lang)}
                    className={`px-2 py-1 rounded text-[10px] font-medium ${templateLang === lang ? 'bg-[#E8720C] text-white' : 'bg-white/5 text-white/40'}`}
                    data-testid={`create-lang-${lang}`}
                  >{lang.toUpperCase()}</button>
                ))}
              </div>
              <button onClick={() => { setSelectedTemplate(null); setSubject(''); setHtmlBody(''); }}
                className="p-1 rounded hover:bg-white/10"><X className="w-3 h-3 text-white/40" /></button>
            </div>
          )}

          {/* Subject */}
          <div className="glass-card p-4">
            <label className="block text-[10px] text-white/30 uppercase tracking-wider mb-2">Subject Line</label>
            <input value={subject} onChange={e => setSubject(e.target.value)}
              className="w-full input-glass" placeholder="Hola {{name}}, tenemos algo para ti"
              data-testid="campaign-subject" />
            <p className="text-[10px] text-white/25 mt-1">Use {'{{name}}'} and {'{{email}}'} for personalization</p>
          </div>

          {/* Body */}
          <div className="glass-card p-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] text-white/30 uppercase tracking-wider">Email Body (HTML)</label>
              <div className="flex gap-2">
                <button onClick={() => setShowPreview(!showPreview)}
                  className="text-[10px] text-[#E8720C] hover:underline flex items-center gap-1" data-testid="campaign-preview-toggle">
                  <Eye className="w-3 h-3" /> {showPreview ? 'Edit' : 'Preview'}
                </button>
              </div>
            </div>
            {showPreview ? (
              <div className="rounded-xl overflow-hidden border border-white/5" style={{ background: '#0a0a0c' }}>
                <div style={{ maxWidth: '500px', margin: '0 auto', padding: '24px' }}>
                  <div style={{ color: '#ccc', fontSize: '14px' }}
                    dangerouslySetInnerHTML={{ __html: previewHTML }}
                    data-testid="campaign-preview" />
                </div>
              </div>
            ) : (
              <textarea value={htmlBody} onChange={e => setHtmlBody(e.target.value)}
                className="w-full input-glass min-h-[200px] text-xs font-mono" placeholder="<div>Your email content...</div>"
                data-testid="campaign-body" />
            )}
          </div>

          {/* Recipients */}
          <div className="glass-card p-4">
            <label className="block text-[10px] text-white/30 uppercase tracking-wider mb-3">Recipients</label>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 mb-3">
              {[
                { id: 'all_verified', label: 'All Verified', icon: Users, desc: 'Users + Leads' },
                { id: 'verified_only', label: 'Users Only', icon: Users, desc: 'Registered users' },
                { id: 'leads_only', label: 'Leads Only', icon: UserPlus, desc: 'Not converted' },
                { id: 'all_leads', label: 'All Leads', icon: UserPlus, desc: 'Verified leads' },
                { id: 'selected', label: 'Select Contacts', icon: CheckCircle, desc: `${selectedEmails.length} selected` },
              ].map(opt => (
                <button key={opt.id}
                  onClick={() => {
                    setRecipientType(opt.id);
                    if (opt.id === 'selected') { setShowContactPicker(true); loadContacts(); }
                    if (opt.id !== 'selected') setSelectedEmails([]);
                  }}
                  className={`p-3 rounded-lg text-left transition-all flex items-start gap-2 ${
                    recipientType === opt.id
                      ? 'bg-[#E8720C]/10 border border-[#E8720C]/25 text-[#E8720C]'
                      : 'bg-white/5 border border-white/10 text-white/50 hover:border-white/20'
                  }`}
                  data-testid={`campaign-recipient-${opt.id}`}
                >
                  <opt.icon className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-medium">{opt.label}</p>
                    <p className="text-[10px] opacity-60">{opt.desc}</p>
                  </div>
                </button>
              ))}
            </div>

            {recipientType !== 'selected' && (
              <input value={countryFilter} onChange={e => setCountryFilter(e.target.value)}
                className="w-full input-glass text-xs mb-2" placeholder="Filter by country (optional)"
                data-testid="campaign-country-filter" />
            )}

            {recipientCount !== null && (
              <p className="text-xs text-[#E8720C] font-medium" data-testid="campaign-recipient-count">
                {recipientCount} recipient{recipientCount !== 1 ? 's' : ''} will receive this campaign
              </p>
            )}
          </div>

          {/* ─── CONTACT PICKER PANEL ─── */}
          {showContactPicker && (
            <div className="glass-card p-4" data-testid="contact-picker-panel">
              <div className="flex items-center justify-between mb-3">
                <label className="text-[10px] text-white/30 uppercase tracking-wider">Select Contacts</label>
                <button onClick={() => setShowContactPicker(false)} className="p-1 rounded hover:bg-white/10">
                  <ChevronUp className="w-4 h-4 text-white/40" />
                </button>
              </div>

              {/* Search */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
                <input value={contactSearch} onChange={e => setContactSearch(e.target.value)}
                  className="w-full input-glass pl-9 text-xs" placeholder="Search by name, email, or country..."
                  data-testid="contact-search" />
              </div>

              {loadingContacts ? (
                <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-white/30" /></div>
              ) : (
                <>
                  {/* Select All */}
                  <div className="flex items-center gap-2 mb-2 pb-2 border-b border-white/5">
                    <input type="checkbox"
                      checked={filteredContacts.length > 0 && filteredContacts.every(c => selectedEmails.includes(c.email))}
                      onChange={toggleAllContacts}
                      className="rounded border-white/20 bg-white/5 text-[#E8720C]"
                      data-testid="contact-select-all" />
                    <span className="text-xs text-white/50">Select All ({filteredContacts.length})</span>
                    {selectedEmails.length > 0 && (
                      <span className="ml-auto text-[10px] text-[#E8720C] font-medium">{selectedEmails.length} selected</span>
                    )}
                  </div>

                  {/* Contact List */}
                  <div className="max-h-[240px] overflow-y-auto space-y-1 pr-1" data-testid="contact-list">
                    {filteredContacts.length === 0 ? (
                      <p className="text-white/30 text-xs text-center py-4">No contacts found</p>
                    ) : filteredContacts.map(c => (
                      <label key={c.email}
                        className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all ${
                          selectedEmails.includes(c.email) ? 'bg-[#E8720C]/5 border border-[#E8720C]/15' : 'bg-white/[0.02] border border-transparent hover:bg-white/5'
                        }`}
                        data-testid={`contact-row-${c.email}`}
                      >
                        <input type="checkbox"
                          checked={selectedEmails.includes(c.email)}
                          onChange={() => toggleContact(c.email)}
                          className="rounded border-white/20 bg-white/5 text-[#E8720C] shrink-0"
                          data-testid={`contact-check-${c.email}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate">{c.name || 'Unknown'}</p>
                          <p className="text-[10px] text-white/40 truncate">{c.email}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {c.country && (
                            <span className="text-[9px] text-white/30 flex items-center gap-1">
                              <Globe className="w-2.5 h-2.5" />{c.country}
                            </span>
                          )}
                          <span className={`text-[9px] px-1.5 py-0.5 rounded border ${
                            c.type === 'user' ? 'text-green-400 bg-green-500/10 border-green-500/20' : 'text-blue-400 bg-blue-500/10 border-blue-500/20'
                          }`}>{c.type}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Sender */}
          <div className="glass-card p-4">
            <label className="block text-[10px] text-white/30 uppercase tracking-wider mb-2">Send From</label>
            <div className="flex gap-2">
              {['notification', 'delivery'].map(s => (
                <button key={s} onClick={() => setSenderType(s)}
                  className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${senderType === s ? 'bg-[#E8720C] text-white' : 'bg-white/5 text-white/50 border border-white/10'}`}
                  data-testid={`campaign-sender-${s}`}
                >{s === 'notification' ? 'Support Email' : 'Delivery Email'}</button>
              ))}
            </div>
          </div>

          {/* Test Email */}
          <div className="glass-card p-4">
            <label className="block text-[10px] text-white/30 uppercase tracking-wider mb-2">Send Test Email First</label>
            <div className="flex gap-2">
              <input value={testEmail} onChange={e => setTestEmail(e.target.value)}
                className="flex-1 input-glass text-xs" placeholder="your@email.com"
                data-testid="campaign-test-email" />
              <button onClick={() => handleSend('test')} disabled={sending || !testEmail}
                className="px-4 py-2 rounded-lg text-xs font-semibold bg-[#E8720C]/15 border border-[#E8720C]/30 text-[#E8720C] disabled:opacity-40 flex items-center gap-2"
                data-testid="campaign-send-test">
                {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                Send Test
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button onClick={() => handleSend('save')} disabled={sending}
              className="px-6 py-3 btn-secondary text-sm" data-testid="campaign-save-draft">
              Save Draft
            </button>
            <button onClick={() => handleSend('send')} disabled={sending || !recipientCount}
              className="flex-1 btn-primary py-3 text-sm font-semibold flex items-center justify-center gap-2" data-testid="campaign-send-all">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send to {recipientCount || 0} Recipients
            </button>
          </div>

          {/* Result */}
          {result && (
            <div className={`p-4 rounded-xl text-sm flex items-center gap-2 ${result.error ? 'bg-red-500/10 border border-red-500/20 text-red-400' : 'bg-green-500/10 border border-green-500/20 text-green-400'}`}
              data-testid="campaign-result">
              {result.error ? <XCircle className="w-4 h-4 shrink-0" /> : <CheckCircle className="w-4 h-4 shrink-0" />}
              {result.error || `Campaign ${result.status}! ${result.sent_count || 0} emails sent.`}
            </div>
          )}
        </div>
      )}

      {/* ─── HISTORY TAB ─── */}
      {tab === 'history' && (
        <div className="space-y-2" data-testid="campaigns-history">
          {history.length === 0 ? (
            <p className="text-white/30 text-sm text-center py-8">No campaigns sent yet.</p>
          ) : history.map((c, i) => (
            <div key={i} className="glass-card p-4 flex items-center gap-4" data-testid={`campaign-history-${i}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                c.status === 'sent' ? 'bg-green-500/15 text-green-400' : c.status === 'test_sent' ? 'bg-yellow-500/15 text-yellow-400' : 'bg-white/5 text-white/30'
              }`}>
                <Mail className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{c.subject || 'No subject'}</p>
                <div className="flex items-center gap-3 text-xs text-white/40 mt-1">
                  <span className={c.status === 'sent' ? 'text-green-400' : c.status === 'test_sent' ? 'text-yellow-400' : ''}>{c.status}</span>
                  <span>{c.sent_count} sent</span>
                  <span>{c.recipient_type}</span>
                  <span>{c.sender_type}</span>
                </div>
              </div>
              <div className="text-xs text-white/30 shrink-0">
                {c.sent_at ? new Date(c.sent_at).toLocaleDateString() : c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CampaignsPanelV2;
